<template>
    <div class="enps-low-ratings">
        <div class="enps-low-ratings__header">
            <div class="enps-low-ratings__title">Последние низкие оценки</div>
            <div class="enps-low-ratings__count">{{ ratings.length }}</div>
        </div>

        <div v-if="ratings.length === 0" class="enps-low-ratings__empty">Низких оценок за период нет</div>

        <div v-else class="enps-low-ratings__rows">
            <a
                v-for="item in ratings"
                :key="item.requestId"
                :href="`/request/store/${item.requestId}`"
                target="_blank"
                rel="noopener"
                class="enps-low-ratings__row"
                :class="rowClass(item.rating)"
            >
                <span class="enps-low-ratings__head">
                    <span class="enps-low-ratings__id">Обращение №{{ item.requestId }}</span>
                    <span class="enps-low-ratings__date">{{ formatClosedAt(item.closedAt) }}</span>
                    <span class="enps-low-ratings__value" :class="ratingClass(item.rating)">{{ item.rating }}</span>
                </span>

                <span class="enps-low-ratings__service">{{ item.categoryTitle }} / {{ item.serviceTitle }}</span>

                <!-- Автор с подразделением идёт первым: блок стоит под разрезом по
                     подразделениям заявителей, и строку читают именно от жалующегося. -->
                <span class="enps-low-ratings__people">
                    {{ item.authorDisplayName
                    }}<template v-if="item.authorDepartment"> ({{ item.authorDepartment }})</template> →
                    {{ item.executorDisplayName }}
                </span>

                <span class="enps-low-ratings__comment" :class="{ 'enps-low-ratings__comment_empty': !item.comment }">
                    {{ item.comment || 'без комментария' }}
                </span>
            </a>
        </div>
    </div>
</template>
<script>
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop } from 'vue-property-decorator';

// Лента последних низких оценок (<= 3) за выбранный период — конкретные случаи под агрегатами.
// «Проблемные категории» выше отвечают, ГДЕ систематически плохо; здесь видно, что именно
// произошло, и каждая строка ведёт в обращение.
//
// Порог у этих двух блоков разный намеренно: «Проблемные категории» считают низкими только
// 1-2 (доля от нейтральных троек размывалась бы), а лента показывает и тройки.
//
// Данные приходят в составе сводки (recentLowRatings), а не отдельным запросом: они зависят
// ровно от тех же фильтров периода и отдела, что и всё на этой вкладке.
@Component
class EnpsLowRatingsList extends Vue {
    @Prop({ type: Array, default: () => [] })
    ratings;

    // Та же пороговая раскраска (4-5 / 3 / 1-2), что в панели разбора оценок. В этой ленте
    // «good» не встречается — четвёрок и пятёрок тут нет, — но шкала общая, чтобы тройка
    // красилась одинаково на всех экранах дашборда.
    rowClass(rating) {
        if (rating >= 4) {
            return 'enps-low-ratings__row_good';
        }

        return rating === 3 ? 'enps-low-ratings__row_mid' : 'enps-low-ratings__row_bad';
    }

    ratingClass(rating) {
        if (rating >= 4) {
            return 'enps-low-ratings__value_good';
        }

        return rating === 3 ? 'enps-low-ratings__value_mid' : 'enps-low-ratings__value_bad';
    }

    formatClosedAt(value) {
        if (!value) {
            return '';
        }

        const [datePart] = value.split(' ');
        const [year, month, day] = datePart.split('-');

        return `${day}.${month}.${year}`;
    }
}

export default EnpsLowRatingsList;
</script>
<style lang="scss">
.enps-low-ratings {
    padding: 15px;
    border-radius: 8px;
    background-color: var(--color-white);
    box-shadow: 0px 1px 10px rgba(88, 88, 88, 0.15);

    &__header {
        display: flex;
        align-items: baseline;
        gap: 8px;
        margin-bottom: 10px;
    }

    &__title {
        font-weight: 600;
    }

    &__count {
        color: #a0a0a0;
        font-size: 0.8125rem;
    }

    // Лента ограничена 25 строками, но на узком экране и они не помещаются — своя прокрутка,
    // как в списке «Все оценки» карточки исполнителя, чтобы блок не растягивал страницу.
    &__rows {
        max-height: 420px;
        overflow-y: auto;
    }

    &__row {
        display: block;
        padding: 8px 12px;
        // Цветная кромка по баллу — при прокрутке единицы видны боковым зрением, без чтения
        // цифр. Та же шкала, что у бейджа оценки.
        border-left: 3px solid transparent;
        border-bottom: 1px solid #e4eaef;
        color: inherit;
        text-decoration: none;
        cursor: pointer;
        transition: background-color 0.15s;

        &_good {
            border-left-color: #00a55a;
        }

        &_mid {
            border-left-color: #f0a30a;
        }

        &_bad {
            border-left-color: #f44336;
        }

        &:last-child {
            border-bottom: none;
        }

        &:hover {
            background-color: #faf6f2;

            .enps-low-ratings__id {
                text-decoration: underline;
            }
        }
    }

    &__head {
        display: flex;
        align-items: center;
        gap: 8px;
    }

    // Тёмный жжёный оттенок акцента (ACCENT_DEEP из enpsColors.js): это ссылка, а не оценка;
    // светлый корп-оранж как цвет текста плохо читается.
    &__id {
        color: #c05a1f;
        font-weight: 600;
    }

    &__date {
        flex: 1;
        color: #a0a0a0;
        font-size: 0.8125rem;
    }

    &__value {
        min-width: 24px;
        padding: 1px 7px;
        border-radius: 10px;
        color: #fff;
        font-size: 0.8125rem;
        font-weight: 600;
        text-align: center;

        &_good {
            background-color: #00a55a;
        }

        &_mid {
            background-color: #f0a30a;
        }

        &_bad {
            background-color: #f44336;
        }
    }

    &__service,
    &__people {
        display: block;
        margin-top: 2px;
        color: #767676;
        font-size: 0.8125rem;
    }

    &__comment {
        display: block;
        margin-top: 4px;
        font-size: 0.875rem;

        &_empty {
            color: #a0a0a0;
            font-style: italic;
        }
    }

    &__empty {
        padding: 20px 12px;
        text-align: center;
        color: #a0a0a0;
    }
}
</style>
